import React, { useState, useEffect } from "react";
import styled, { keyframes, css } from "styled-components";
import { colors } from "../theme";
import { FiMapPin, FiClock, FiExternalLink, FiX, FiStar, FiPlus } from "react-icons/fi";
import { getPoiDetails, getPoisByCity } from "../data/poiDatabase";

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { transform: translateY(50px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

// Fallback image URL
const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1515027333344-9c34c377f51e?w=400&h=200&fit=crop';
const FALLBACK_IMAGES = {
  default: 'https://images.unsplash.com/photo-1531403943-332222688857-15952bccc8c7?w=400&h=200&fit=crop',
  travel: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=400&h=200&fit=crop',
  city: 'https://images.unsplash.com/photo-1562696128-6f803b61c63c?w=400&h=200&fit=crop',
  culture: 'https://images.unsplash.com/photo-1515027333344-9c34c377f51e?w=400&h=200&fit=crop'
};

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 2rem;
  margin: 2.5rem 0 1.5rem 0;
  padding: 0 1rem;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  height: 100%;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
    
    img {
      transform: scale(1.05);
    }
  }
`;
const ImageContainer = styled.div`
  width: 100%;
  height: 180px;
  overflow: hidden;
  position: relative;
`;

const Img = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
  display: block;
`;

const Badge = styled.span`
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(255, 255, 255, 0.9);
  color: ${colors.primary};
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  backdrop-filter: blur(4px);
  z-index: 2;
`;

const Rating = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  bottom: 12px;
  left: 12px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.85rem;
  backdrop-filter: blur(4px);
  
  svg {
    color: #FFD700;
    margin-right: 4px;
  }
`;
const CardBody = styled.div`
  padding: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;
const Title = styled.h3`
  margin: 0 0 0.5rem 0;
  color: ${colors.primary};
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1.3;
`;
const Desc = styled.p`
  color: ${colors.muted};
  font-size: 0.95rem;
  margin: 0 0 1rem 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  color: ${colors.muted};
  font-size: 0.9rem;
  
  svg {
    margin-right: 6px;
    color: ${colors.accent};
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 1rem;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const PrimaryButton = styled(Button)`
  background: ${colors.accent};
  color: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: ${colors.primary};
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
  }
`;

const SecondaryButton = styled(Button)`
  background: white;
  color: ${colors.primary};
  border: 1px solid ${colors.border};
  margin-right: 0.75rem;
  
  &:hover {
    background: #f8f9fa;
    border-color: ${colors.primary};
  }
`;
const Tags = styled.div`
  margin-bottom: 0.7rem;
`;
const Tag = styled.span`
  display: inline-block;
  background: ${colors.highlight};
  color: ${colors.primary};
  border-radius: 0.7rem;
  font-size: 0.93rem;
  padding: 0.23rem 0.9rem;
  margin-right: 0.5rem;
  margin-bottom: 0.2rem;
`;
const AddBtn = styled.button`
  background: ${colors.accent};
  color: #fff;
  border: none;
  border-radius: 0.7rem;
  font-size: 1.02rem;
  font-weight: 600;
  padding: 0.7rem 0;
  width: 100%;
  margin-top: 0.7rem;
  cursor: pointer;
  transition: background 0.13s;
  &:hover { background: ${colors.primary}; }
`;
const TabList = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
`;
const Banner = styled.div`
  padding: 0.5rem 1rem;
  margin: 0 1rem 1rem;
  border-radius: 8px;
  background: linear-gradient(90deg, #E31B23, #FFD700, #006E33);
  color: white;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  
  h2 {
    margin: 0 0 2px 0;
    font-size: 1.1rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    line-height: 1.3;
    
    @media (max-width: 768px) {
      font-size: 1rem;
      flex-wrap: wrap;
      gap: 0.25rem 0.5rem;
    }
  }
  
  .divider {
    opacity: 0.7;
    font-weight: 300;
    @media (max-width: 768px) {
      display: inline;
    }
  }
  
  .promo-text {
    font-size: 0.75rem;
    opacity: 0.9;
    margin-top: 0.2rem;
    display: block;
    font-style: italic;
    line-height: 1.3;
    
    @media (max-width: 480px) {
      font-size: 0.7rem;
    }
  }
  
  .highlight {
    font-weight: 600;
    color: #FFD700;
    text-shadow: 0 0 2px rgba(0,0,0,0.3);
  }
`;

const TabItem = styled.button`
  background: ${({ active }) => active ? colors.primary : "#fff"};
  color: ${({ active }) => active ? "#fff" : colors.text};
  border: 1px solid ${colors.primary};
  border-radius: 0.7rem;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-weight: 600;
  &:hover {
    background: ${colors.primary};
    color: #fff;
  }
`;
const CitySection = styled.div`
  margin-bottom: 3rem;
`;
const CityTitle = styled.h2`
  color: ${colors.primary};
  margin: 2rem 0 1rem;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  backdrop-filter: blur(5px);
  
  ${({ isOpen }) => isOpen && css`
    opacity: 1;
    visibility: visible;
  `}
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transform: translateY(20px);
  opacity: 0;
  transition: all 0.3s ease;
  
  ${({ isOpen }) => isOpen && css`
    transform: translateY(0);
    opacity: 1;
  `}
`;

const ModalHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid ${colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: ${colors.primary};
  font-size: 1.75rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: ${colors.muted};
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f5f5f5;
    color: ${colors.primary};
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
`;

const ModalImage = styled.div`
  height: 300px;
  width: 100%;
  overflow: hidden;
  position: relative;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ModalSection = styled.div`
  margin-bottom: 1.5rem;
  
  h3 {
    margin: 0 0 1rem 0;
    color: ${colors.primary};
    font-size: 1.25rem;
    display: flex;
    align-items: center;
    
    svg {
      margin-right: 8px;
    }
  }
  
  p {
    margin: 0 0 1rem 0;
    line-height: 1.6;
    color: ${colors.text};
  }
`;

const ModalFooter = styled.div`
  padding: 1.5rem;
  border-top: 1px solid ${colors.border};
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;

// Removed duplicate Tag component

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0.5rem 0 1rem;
`;

// Generate tags based on POI data
const generateTags = (poi) => {
  const tags = [];
  if (poi.category) tags.push(poi.category);
  if (poi.priceLevel) tags.push(poi.priceLevel);
  if (poi.duration) tags.push(poi.duration);
  return tags;
};

export default function Explore({ onAdd, activitiesByCity }) {
  const cityList = Object.keys(activitiesByCity);
  const [selectedCity, setSelectedCity] = React.useState(cityList[0] || "");
  const [imageErrors, setImageErrors] = React.useState({});
  const [imageLoaded, setImageLoaded] = React.useState({});
  const [selectedPoi, setSelectedPoi] = React.useState(null);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const activities = activitiesByCity[selectedCity] || [];

  // Set up image loading states
  React.useEffect(() => {
    const newImageLoaded = {};
    activities.forEach(act => {
      newImageLoaded[act.title] = false;
    });
    setImageLoaded(newImageLoaded);
  }, [activities]);

  // Generate Google Maps URL for a POI
  const getGoogleMapsUrl = (poi) => {
    // If we have coordinates, use them for more accurate results
    if (poi.lat && poi.lng) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(poi.title)}&query_place_id=${poi.placeId || ''}`;
    }
    // Otherwise, search by name and city
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${poi.title}, ${poi.city || selectedCity}, Portugal`)}`;
  };

  // Kept for potential future use
  const openPoiDetails = (activity) => {
    // First try to get the full POI details by title or ID
    let fullPoi = getPoiDetails(activity.id) || activity;
    
    // If we don't have all the details, try to find a match in the POI database
    if (!fullPoi.description || !fullPoi.features) {
      const allPois = getPoisByCity(selectedCity);
      const matchedPoi = allPois.find(p => 
        p.id === activity.id || 
        p.title.toLowerCase() === activity.title.toLowerCase()
      );
      
      if (matchedPoi) {
        fullPoi = { ...fullPoi, ...matchedPoi };
      }
    }
    
    // Ensure we have all required fields with defaults
    fullPoi = {
      id: fullPoi.id || activity.id,
      title: fullPoi.title || activity.title,
      description: fullPoi.description || fullPoi.desc || activity.desc || 'No description available',
      lat: fullPoi.lat || activity.lat,
      lng: fullPoi.lng || activity.lng,
      city: fullPoi.city || selectedCity,
      website: fullPoi.website || activity.website,
      rating: fullPoi.rating || activity.rating,
      images: fullPoi.images || (activity.imageUrl ? [activity.imageUrl] : []),
      features: fullPoi.features || {},
      tips: fullPoi.tips || [],
      bestTimeToVisit: fullPoi.bestTimeToVisit || [],
      averageVisitDuration: fullPoi.averageVisitDuration || 60,
      reviewCount: fullPoi.reviewCount || 0,
      lastUpdated: fullPoi.lastUpdated || new Date().toISOString().split('T')[0],
      phone: fullPoi.phone || fullPoi.contact?.phone || '',
      email: fullPoi.email || fullPoi.contact?.email || '',
      categories: fullPoi.categories || fullPoi.category || [],
      price: fullPoi.price || { level: 2, range: '€€' },
      hours: fullPoi.hours || {
        Monday: '9:00 AM - 6:00 PM',
        Tuesday: '9:00 AM - 6:00 PM',
        Wednesday: '9:00 AM - 6:00 PM',
        Thursday: '9:00 AM - 6:00 PM',
        Friday: '9:00 AM - 6:00 PM',
        Saturday: '10:00 AM - 4:00 PM',
        Sunday: 'Closed'
      }
    };
    
    setSelectedPoi(fullPoi);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = 'auto';
    setTimeout(() => setSelectedPoi(null), 300);
  };

  // Handle click outside modal
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  // Close modal on Escape key
  React.useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    };
    
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  // Handle image loading errors
  const handleImageError = (title) => {
    setImageErrors(prev => ({
      ...prev,
      [title]: true
    }));
  };

  // Handle successful image load
  const handleImageLoad = (title) => {
    setImageLoaded(prev => ({
      ...prev,
      [title]: true
    }));
  };

  // Get the appropriate image URL for a POI
  const getImageUrl = (activity) => {
    // First check if we have a direct image URL in the activity
    if (activity.images && activity.images.length > 0 && !imageErrors[activity.title]) {
      return activity.images[0];
    }
    
    // Then check for imageUrl
    if (activity.imageUrl && !imageErrors[activity.title]) {
      return activity.imageUrl;
    }
    
    // Fallback to Google Maps Static API if we have coordinates
    if (activity.lat && activity.lng) {
      return `https://maps.googleapis.com/maps/api/staticmap?center=${activity.lat},${activity.lng}&zoom=15&size=400x200&maptype=roadmap&markers=color:red%7C${activity.lat},${activity.lng}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;
    }
    
    // Fallback to a themed placeholder based on activity type or city
    const city = activity.city || '';
    if (city.toLowerCase().includes('beach') || (activity.title && activity.title.toLowerCase().includes('beach'))) {
      return 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&h=400&fit=crop';
    } else if (city.toLowerCase().includes('museu') || (activity.title && activity.title.toLowerCase().includes('museum'))) {
      return 'https://images.unsplash.com/photo-1575224300303-3babc1ab122e?w=800&h=400&fit=crop';
    } else {
      const searchTerm = activity.title ? encodeURIComponent(activity.title) : encodeURIComponent(city || 'portugal');
      return `https://source.unsplash.com/800x400/?${searchTerm},landmark`;
    }
  };

  // Filter out 'Faro' and 'Additional' from the city list
  const filteredCityList = cityList.filter(city => 
    !['Faro', 'Additional'].includes(city)
  );

  // If the currently selected city is filtered out, select the first available city
  React.useEffect(() => {
    if (filteredCityList.length > 0 && !filteredCityList.includes(selectedCity)) {
      setSelectedCity(filteredCityList[0]);
    }
  }, [filteredCityList, selectedCity]);

  return (
    <>
      <Banner>
        <h2>
          <span>Discover Portugal</span>
          <span className="divider">•</span>
          <span>{activities.length} Activities</span>
          <span className="divider">•</span>
          <span>{selectedCity}</span>
        </h2>
        <span className="promo-text">
          <span className="highlight">Explore:</span> Click on any card to see details and add to your itinerary
        </span>
      </Banner>
      <TabList>
        {filteredCityList.map((city) => (
          <TabItem
            key={city}
            active={selectedCity === city}
            onClick={() => setSelectedCity(city)}
          >
            {city}
          </TabItem>
        ))}
      </TabList>
      <CitySection>
        <CityTitle>{selectedCity}</CityTitle>
        <Grid>
          {activities.map(act => (
            <Card key={act.title} onClick={() => {}}>
              <ImageContainer>
                <Img
                  src={getImageUrl(act)}
                  alt={act.title}
                  onError={() => handleImageError(act.title)}
                  onLoad={() => handleImageLoad(act.title)}
                  style={{
                    opacity: imageLoaded[act.title] ? 1 : 0,
                    transition: 'opacity 0.3s ease-in-out'
                  }}
                />
                {!imageLoaded[act.title] && (
                  <Img
                    src={FALLBACK_IMAGE}
                    alt="Loading..."
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      zIndex: 1
                    }}
                  />
                )}
                {act.rating && (
                  <Rating>
                    <FiStar />
                    <span>{act.rating}</span>
                  </Rating>
                )}
                {act.category && <Badge>{act.category}</Badge>}
              </ImageContainer>
              <CardBody>
                <Title>{act.title}</Title>
                <Desc>{act.desc || 'No description available.'}</Desc>
                <Actions>
                  <div style={{ display: 'flex', gap: '0.5rem', width: '100%' }}>
                    <SecondaryButton 
                      as="a"
                      href={getGoogleMapsUrl(act)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ 
                        flex: 1, 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textDecoration: 'none'
                      }}
                    >
                      <FiMapPin style={{ marginRight: '6px' }} />
                      See Details
                    </SecondaryButton>
                    <PrimaryButton 
                      onClick={(e) => {
                        e.stopPropagation();
                        onAdd({ 
                          title: act.title, 
                          desc: act.desc, 
                          lat: act.lat, 
                          lng: act.lng, 
                          city: selectedCity, 
                          website: act.website, 
                          rating: act.rating 
                        });
                      }}
                      style={{ flex: 1 }}
                    >
                      <FiPlus /> Add
                    </PrimaryButton>
                  </div>
                </Actions>
              </CardBody>
            </Card>
          ))}
        </Grid>
      </CitySection>

      {/* POI Details Modal */}
      <Modal 
        isOpen={isModalOpen} 
        onClick={handleBackdropClick}
      >
        <ModalContent isOpen={isModalOpen}>
          {selectedPoi && (
            <>
              <ModalHeader>
                <div style={{ display: 'flex', alignItems: 'flex-start', padding: '1.5rem' }}>
                  <div style={{ 
                    width: '120px', 
                    height: '120px', 
                    borderRadius: '12px', 
                    overflow: 'hidden',
                    flexShrink: 0,
                    marginRight: '1.5rem',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}>
                    <img 
                      src={selectedPoi.images?.[0] || selectedPoi.imageUrl || `https://source.unsplash.com/800x400/?${encodeURIComponent(selectedPoi.title || 'lisbon')}`} 
                      alt={selectedPoi.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block'
                      }}
                      onError={(e) => {
                        e.target.onerror = null;
                        const city = selectedPoi.city || 'lisbon';
                        const title = selectedPoi.title ? encodeURIComponent(selectedPoi.title) : '';
                        e.target.src = `https://source.unsplash.com/800x800/?${title},${city},landmark`;
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <ModalTitle style={{ marginTop: '0.5rem', marginBottom: '1rem' }}>{selectedPoi.title}</ModalTitle>
                    {selectedPoi.address && (
                      <ModalAddress style={{ marginBottom: '1rem' }}>
                        <FiMapPin size={14} style={{ marginRight: '6px', flexShrink: 0 }} />
                        {selectedPoi.address}
                      </ModalAddress>
                    )}
                    
                    {/* Features Section Moved Here */}
                    {selectedPoi.features && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                          {Object.entries(selectedPoi.features).map(([feature, isAvailable]) => (
                            isAvailable && (
                              <div 
                                key={feature}
                                style={{
                                  background: colors.highlight,
                                  padding: '0.4rem 0.8rem',
                                  borderRadius: '20px',
                                  fontSize: '0.8em',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.4rem',
                                  color: colors.primary,
                                  fontWeight: '500'
                                }}
                              >
                                {feature === 'wheelchairAccess' && '♿'}
                                {feature === 'parking' && '🅿️'}
                                {feature === 'guidedTours' && '👥'}
                                {feature === 'audioGuide' && '🎧'}
                                {feature === 'giftShop' && '🛍️'}
                                {feature === 'cafe' && '☕'}
                                {feature === 'restrooms' && '🚻'}
                                {feature.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                              </div>
                            )
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </ModalHeader>
              <ModalBody>
                {/* Rating and Categories */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.5rem', alignItems: 'center' }}>
                  {selectedPoi.rating !== undefined && (
                    <div style={{ 
                      display: 'inline-flex',
                      alignItems: 'center',
                      background: '#fff8e1',
                      padding: '0.4rem 1rem',
                      borderRadius: '20px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                      <FiStar style={{ color: '#FFD700', marginRight: '6px' }} />
                      <span style={{ fontWeight: '600' }}>{selectedPoi.rating?.toFixed?.(1) || 'N/A'}</span>
                      {selectedPoi.reviewCount > 0 && (
                        <span style={{ color: '#666', marginLeft: '6px', fontSize: '0.9em' }}>
                          ({selectedPoi.reviewCount?.toLocaleString?.() || 0} reviews)
                        </span>
                      )}
                    </div>
                  )}
                  
                  {/* Categories */}
                  {(selectedPoi.categories || selectedPoi.category) && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {(selectedPoi.categories || selectedPoi.category || []).map((category, i) => (
                        <div key={i} style={{
                          background: colors.highlight,
                          padding: '0.4rem 0.9rem',
                          borderRadius: '20px',
                          fontSize: '0.85em',
                          color: colors.primary,
                          fontWeight: '500'
                        }}>
                          {category}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Price Level */}
                {selectedPoi.price?.level && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      background: '#e8f5e9',
                      padding: '0.4rem 1rem',
                      borderRadius: '20px',
                      color: '#2e7d32',
                      fontSize: '0.9em',
                      fontWeight: '500'
                    }}>
                      Price: {Array(selectedPoi.price.level).fill('€').join('')}
                      {selectedPoi.price.range && ` (${selectedPoi.price.range})`}
                    </div>
                  </div>
                )}
                
                {/* Description */}
                {selectedPoi.description && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ margin: '0 0 0.75rem 0', color: colors.primary }}>About</h3>
                    <p style={{ lineHeight: '1.6', color: '#333' }}>{selectedPoi.description}</p>
                  </div>
                )}

                {/* Contact Information */}
                <div style={{ margin: '1.5rem 0', padding: '1.25rem', background: '#f9f9f9', borderRadius: '12px' }}>
                  <h3 style={{ margin: '0 0 1rem 0', color: colors.primary, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <FiMapPin size={18} /> Contact Information
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1rem' }}>
                    {selectedPoi.phone && (
                      <div>
                        <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '0.25rem' }}>Phone</div>
                        <a href={`tel:${selectedPoi.phone}`} style={{ color: colors.primary, textDecoration: 'none', fontWeight: '500' }}>
                          {selectedPoi.phone}
                        </a>
                      </div>
                    )}
                    {selectedPoi.email && (
                      <div>
                        <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '0.25rem' }}>Email</div>
                        <a href={`mailto:${selectedPoi.email}`} style={{ color: colors.primary, textDecoration: 'none', wordBreak: 'break-word' }}>
                          {selectedPoi.email}
                        </a>
                      </div>
                    )}
                    {selectedPoi.website && (
                      <div>
                        <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '0.25rem' }}>Website</div>
                        <a 
                          href={selectedPoi.website.startsWith('http') ? selectedPoi.website : `https://${selectedPoi.website}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: colors.primary, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
                        >
                          {selectedPoi.website.replace(/^https?:\/\//, '')} <FiExternalLink size={12} />
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* Opening Hours */}
                {selectedPoi.hours && (
                  <div style={{ margin: '1.5rem 0' }}>
                    <h3 style={{ margin: '0 0 1rem 0', color: colors.primary, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <FiClock size={18} /> Opening Hours
                    </h3>
                    <div style={{ 
                      background: '#fff', 
                      borderRadius: '8px', 
                      border: '1px solid #eee',
                      overflow: 'hidden'
                    }}>
                      {Object.entries(selectedPoi.hours).map(([day, hours], index, arr) => (
                        <div key={day} style={{ 
                          display: 'flex', 
                          padding: '0.75rem 1rem',
                          borderBottom: index === arr.length - 1 ? 'none' : '1px solid #f0f0f0'
                        }}>
                          <div style={{ width: '100px', fontWeight: '500' }}>{day.charAt(0).toUpperCase() + day.slice(1)}</div>
                          <div>{hours === 'Closed' ? 'Closed' : hours || 'Not specified'}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tips & Best Times */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', margin: '1.5rem 0' }}>
                  {/* Tips Section */}
                  {selectedPoi.tips && selectedPoi.tips.length > 0 && (
                    <div>
                      <h3 style={{ margin: '0 0 1rem 0', color: colors.primary, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span role="img" aria-label="lightbulb">💡</span> Visitor Tips
                      </h3>
                      <div style={{ 
                        background: '#fff', 
                        borderRadius: '8px', 
                        border: '1px solid #eee',
                        padding: '1rem'
                      }}>
                        <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                          {selectedPoi.tips.map((tip, index) => (
                            <li key={index} style={{ marginBottom: '0.5rem', lineHeight: '1.5' }}>{tip}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Best Times Section */}
                  {selectedPoi.bestTimeToVisit && (
                    <div>
                      <h3 style={{ margin: '0 0 1rem 0', color: colors.primary, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <FiClock size={18} /> Best Time to Visit
                      </h3>
                      <div style={{ 
                        background: '#fff', 
                        borderRadius: '8px', 
                        border: '1px solid #eee',
                        padding: '1rem'
                      }}>
                        {Array.isArray(selectedPoi.bestTimeToVisit) ? (
                          <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                            {selectedPoi.bestTimeToVisit.map((time, index) => (
                              <li key={index} style={{ marginBottom: '0.5rem', lineHeight: '1.5' }}>{time}</li>
                            ))}
                          </ul>
                        ) : (
                          <p style={{ margin: 0, lineHeight: '1.6' }}>{selectedPoi.bestTimeToVisit}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Additional Information */}
                <div style={{ 
                  margin: '1.5rem 0', 
                  padding: '1.25rem', 
                  background: '#f9f9f9', 
                  borderRadius: '12px',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: '1.5rem'
                }}>
                  {selectedPoi.averageVisitDuration && (
                    <div>
                      <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '0.25rem' }}>Average Visit Duration</div>
                      <div style={{ fontWeight: '500' }}>
                        {selectedPoi.averageVisitDuration >= 60 
                          ? `${Math.floor(selectedPoi.averageVisitDuration / 60)}h ${selectedPoi.averageVisitDuration % 60 > 0 ? `${selectedPoi.averageVisitDuration % 60}m` : ''}`.trim()
                          : `${selectedPoi.averageVisitDuration} minutes`
                        }
                      </div>
                    </div>
                  )}
                  
                  {selectedPoi.lastUpdated && (
                    <div>
                      <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '0.25rem' }}>Last Updated</div>
                      <div style={{ fontWeight: '500' }}>
                        {new Date(selectedPoi.lastUpdated).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Image Gallery */}
                {selectedPoi.images && selectedPoi.images.length > 0 && (
                  <div style={{ margin: '2rem 0' }}>
                    <h3 style={{ margin: '0 0 1rem 0', color: colors.primary }}>Gallery</h3>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                      gap: '1rem',
                      marginTop: '1rem'
                    }}>
                      {selectedPoi.images.map((image, index) => (
                        <div 
                          key={index}
                          style={{
                            aspectRatio: '4/3',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            transition: 'transform 0.2s ease',
                            ':hover': {
                              transform: 'scale(1.02)'
                            }
                          }}
                        >
                          <img 
                            src={image} 
                            alt={`${selectedPoi.title} ${index + 1}`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              display: 'block'
                            }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Available';
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </ModalBody>
              
              <ModalFooter>
                <SecondaryButton 
                  as="a" 
                  href={`https://www.google.com/maps/search/?api=1&query=${selectedPoi.lat},${selectedPoi.lng}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <FiMapPin /> View on Map
                </SecondaryButton>
                <PrimaryButton 
                  onClick={() => {
                    onAdd({ 
                      title: selectedPoi.title, 
                      desc: selectedPoi.description, 
                      lat: selectedPoi.lat, 
                      lng: selectedPoi.lng, 
                      city: selectedPoi.city, 
                      website: selectedPoi.website, 
                      rating: selectedPoi.rating 
                    });
                    closeModal();
                  }}
                >
                  <FiPlus /> Add to Itinerary
                </PrimaryButton>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}
